/**
 * Canonical URL Helper for SEO
 * Generates absolute canonical URLs with protocol and domain
 * Fixes Lighthouse SEO issue: "Document does not have a valid rel=canonical (not absolute URL)"
 */

/**
 * Get the base URL from environment variables
 * Falls back to window.location.origin if not configured
 * @returns {string} Base URL (e.g., https://www.onlineselftest.com)
 */
export function getBaseUrl() {
  // Try to get from environment variables first (guard import.meta access)
  let envBaseUrl;
  try {
    // Use optional chaining on import.meta which is valid in ESM and modern build tools.
    // This avoids referencing the bare identifier `import` which causes a parsing error.
    envBaseUrl = import.meta?.env?.VITE_BASE_URL;
  } catch (e) {
    // If import.meta isn't supported in the runtime, fall back gracefully.
    envBaseUrl = undefined;
  }

  if (envBaseUrl && typeof envBaseUrl === 'string' && envBaseUrl.trim()) {
    // Ensure no trailing slash
    return envBaseUrl.replace(/\/$/, '');
  }
  
  // Fallback to current origin (for SSR or when env var is not set)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  // Default fallback
  return 'https://www.onlineselftest.com';
}

/**
 * Generate an absolute canonical URL
 * @param {string} path - Relative path (e.g., '/learn/11th/11th-chemistry/47f0e821')
 * @returns {string} Absolute canonical URL with protocol and domain
 * @example
 * getCanonicalUrl('/learn/11th/11th-chemistry/47f0e821')
 * // Returns: 'https://www.onlineselftest.com/learn/11th/11th-chemistry/47f0e821'
 */
export function getCanonicalUrl(path) {
  const baseUrl = getBaseUrl();
  
  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove any trailing slashes from path
  const normalizedPath = cleanPath.replace(/\/$/, '');
  
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Validate if a URL is absolute (has protocol and domain)
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is absolute (starts with http:// or https://)
 */
export function isAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /^https?:\/\//.test(url);
}

/**
 * Ensure a URL is absolute by converting relative paths to absolute URLs
 * @param {string} url - URL (relative or absolute)
 * @returns {string} Absolute URL with protocol and domain
 */
export function ensureAbsoluteUrl(url) {
  if (isAbsoluteUrl(url)) {
    return url;
  }
  return getCanonicalUrl(url);
}
